import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Fetch all site content in one query and cache it
export const fetchAllContent = async (): Promise<Record<string, { value: string; updated_at: string }>> => {
  const { data, error } = await supabase
    .from("site_content")
    .select("content_key, content_value, updated_at")
    // Landing page documents are loaded only by the landing pages themselves.
    .not("content_key", "like", "landing.%");

  if (error) {
    console.error("Failed to fetch site content:", error);
    return {};
  }

  const map: Record<string, { value: string; updated_at: string }> = {};
  for (const row of data || []) {
    map[row.content_key] = { value: row.content_value, updated_at: row.updated_at };
  }
  return map;
};

export function useAllContent() {
  return useQuery({
    queryKey: ["site-content"],
    queryFn: fetchAllContent,
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 10 * 60 * 1000,
  });
}

export function useContent(key: string, fallback: string): string {
  const { data } = useAllContent();
  return data?.[key]?.value ?? fallback;
}

export function useContentMulti(keys: Record<string, string>): Record<string, string> {
  const { data } = useAllContent();
  const result: Record<string, string> = {};
  for (const [key, fallback] of Object.entries(keys)) {
    result[key] = data?.[key]?.value ?? fallback;
  }
  return result;
}

/**
 * Same as useContentMulti but also returns a `ready` flag that flips true
 * only after the site_content query has resolved (or errored). Useful for
 * hero/above-the-fold copy where we must avoid a flash of the fallback
 * text before the CMS value loads.
 */
export function useContentMultiReady(
  keys: Record<string, string>
): { content: Record<string, string>; ready: boolean } {
  const { data, isFetched, isLoading } = useAllContent();
  const result: Record<string, string> = {};
  for (const [key, fallback] of Object.entries(keys)) {
    result[key] = data?.[key]?.value ?? fallback;
  }
  return { content: result, ready: isFetched && !isLoading };
}
